import { useEffect, useState } from "react";
import type { YinshReplaySnapshot } from "../replay-runner";
import { Board } from "./Board";
import { GameInfo } from "./GameInfo";

/**
 * Shared read-only move-by-move stepper for YINSH replays. Used by both the
 * fixture-driven `ReplayScreen` (dev replay route, `syncUrl` on for screenshot
 * deep-links) and the in-room post-match review overlay (`onExit` provided).
 * Renders the same toolbar + Board + GameInfo and keeps the `yinsh-replay-*`
 * test ids stable across both surfaces.
 */
interface ReplayStepperProps {
  readonly snapshots: readonly YinshReplaySnapshot[];
  readonly whiteName: string;
  readonly blackName: string;
  /** Map of relay userId -> display name (fixture route). Optional; the actor
   * label falls back to the event's player color when absent. */
  readonly namesByPlayerId?: Readonly<Record<string, string>>;
  /** Sync the current step to `?step=`/`?seq=` query params. Off by default so
   * the in-room overlay never rewrites the live game URL. */
  readonly syncUrl?: boolean;
  /** When provided, renders a close control that exits the stepper. */
  readonly onExit?: () => void;
  /** When provided, renders an export control (download the game for training). */
  readonly onExport?: () => void;
  /** When provided, renders a "share link" control (create a replay share). */
  readonly onShare?: () => void;
  /** Whether this viewer drives the stepper. Observers (future multiplayer)
   * pass false to follow the host's position read-only. Defaults to true. */
  readonly isController?: boolean;
}

const PHASE_LABELS: Readonly<Record<string, string>> = {
  RING_PLACEMENT: "링 배치",
  PLAYING: "진행 중",
  REMOVING_ROW: "줄 제거",
  GAME_END: "게임 종료",
};

function actorLabel(
  snapshot: YinshReplaySnapshot,
  namesByPlayerId: Readonly<Record<string, string>>,
  whiteName: string,
  blackName: string,
): string {
  const byId = namesByPlayerId[snapshot.event.userId];
  if (byId) return byId;
  const player = (snapshot.event.payload as { player?: string }).player;
  if (player === "WHITE") return whiteName;
  if (player === "BLACK") return blackName;
  return snapshot.event.userId;
}

function eventSummary(snapshot: YinshReplaySnapshot): string {
  return `#${snapshot.event.sequenceNumber} ${snapshot.event.eventType}`;
}

function clampStepIndex(
  index: number,
  snapshots: readonly YinshReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly YinshReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);

  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly YinshReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayStepper({
  snapshots,
  whiteName,
  blackName,
  namesByPlayerId = {},
  syncUrl = false,
  onExit,
  onExport,
  onShare,
  isController = true,
}: ReplayStepperProps) {
  const [stepIndex, setStepIndex] = useState(() =>
    syncUrl ? resolveStepIndexFromSearch(snapshots) : 0,
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    if (syncUrl) setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots, syncUrl]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    if (syncUrl) replaceStepQuery(nextIndex, snapshots);
  }

  if (!current) return null;

  const state = current.state;
  const maxIndex = snapshots.length - 1;
  const phaseLabel = PHASE_LABELS[state.phase] ?? state.phase;
  const isGameOver = state.phase === "GAME_END";

  return (
    <div
      className="min-h-screen bg-surface-primary"
      data-testid="yinsh-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="yinsh-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm text-text-primary"
              data-testid="yinsh-replay-event-summary"
            >
              {eventSummary(current)} ·{" "}
              {actorLabel(current, namesByPlayerId, whiteName, blackName)}
            </p>
            <p
              className="text-text-secondary text-xs"
              data-testid="yinsh-replay-phase"
            >
              {phaseLabel} · 현재 {state.currentPlayer}
              {isGameOver && state.winner ? ` · 승자 ${state.winner}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="yinsh-replay-prev"
              disabled={!isController || safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="yinsh-replay-step-slider"
              disabled={!isController}
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary disabled:opacity-40"
              data-testid="yinsh-replay-next"
              disabled={!isController || safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="yinsh-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
            {onShare && (
              <button
                type="button"
                className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary"
                data-testid="yinsh-replay-share"
                onClick={onShare}
              >
                공유 링크
              </button>
            )}
            {onExport && (
              <button
                type="button"
                className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm text-text-primary"
                data-testid="yinsh-replay-export"
                onClick={onExport}
              >
                내보내기
              </button>
            )}
            {onExit && (
              <button
                type="button"
                className="rounded-md bg-accent-blue px-3 py-2 font-semibold text-sm text-white"
                data-testid="yinsh-replay-exit"
                onClick={onExit}
              >
                닫기
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:flex-row">
        <aside className="md:w-56">
          <GameInfo
            gameState={state}
            myColor={null}
            whiteName={whiteName}
            blackName={blackName}
          />
          {isGameOver && (
            <div
              className="mt-3 rounded-md border border-border-primary bg-surface-secondary p-3 text-center font-semibold text-sm text-text-primary"
              data-testid="yinsh-replay-game-over"
            >
              게임 종료{state.winner ? ` · 승자 ${state.winner}` : ""}
            </div>
          )}
        </aside>
        <div className="flex-1">
          <Board
            gameState={state}
            selectedRingPosition={null}
            validDestinations={[]}
            onPositionClick={() => undefined}
            disabled={true}
          />
        </div>
      </div>
    </div>
  );
}
